import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ClipboardList, ArrowRight } from "lucide-react";

const LOGO = "/brand/logo.jpg";

const LogoMark = () => {
  const [errored, setErrored] = useState(false);
  if (errored) {
    // Fallback estilizado si la imagen no carga
    return (
      <div className="flex flex-col leading-tight" style={{ color: "var(--sage-700)" }}>
        <span className="handwritten text-2xl md:text-3xl" style={{ lineHeight: 1 }}>Salud y Visión</span>
        <span className="font-sans-body text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: "var(--sage-500)" }}>
          Optometría y Terapia Visual
        </span>
      </div>
    );
  }
  return (
    <img
      src={LOGO}
      alt="Salud y Visión — Optometría y Terapia Visual"
      className="h-20 md:h-28 object-contain"
      style={{ mixBlendMode: "multiply" }}
      onError={() => setErrored(true)}
    />
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const links = [
    { to: "/", label: "Inicio", exact: true },
    { to: "/nosotros", label: "Nosotros" },
    { to: "/servicios", label: "Servicios" },
    { to: "/sintomas", label: "Síntomas" },
    { to: "/centros", label: "Nuestros centros" },
    { to: "/preguntas-frecuentes", label: "FAQ" },
    { to: "/profesionales", label: "Profesionales" },
    { to: "/#contacto", label: "Contacto" },
  ];

  const handleAnchor = (to) => {
    setOpen(false);
    if (to.startsWith("/#") && isHome) {
      const id = to.substring(2);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md" style={{ background: "rgba(246,244,236,0.9)", borderBottom: "1px solid var(--sage-100)" }} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <LogoMark />
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-sans-body">
          {links.map((l) => (
            l.to.startsWith("/#") ? (
              <Link key={l.to} to={l.to} onClick={() => handleAnchor(l.to)} className="nav-link" data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</Link>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.exact} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</NavLink>
            )
          ))}
          <Link to="/#autoevaluacion" className="btn-primary" data-testid="nav-cta">
            <span className="flex flex-col items-start leading-tight">
              <span>Haz tu autoevaluación</span>
              <span className="text-[10px] font-normal opacity-85 hidden lg:block">para saber si necesitas nuestra ayuda</span>
            </span>
          </Link>
        </nav>

        <button onClick={() => setOpen(!open)} className="md:hidden text-[color:var(--sage-600)]" data-testid="mobile-menu-btn">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav className="md:hidden px-6 pb-5 flex flex-col gap-3 font-sans-body" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => handleAnchor(l.to)} className="nav-link py-2" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>{l.label}</Link>
          ))}
          <Link to="/#autoevaluacion" onClick={() => setOpen(false)} className="btn-primary mt-2" data-testid="mobile-nav-cta">
            <span className="flex flex-col items-start leading-tight">
              <span>Haz tu autoevaluación</span>
              <span className="text-[10px] font-normal opacity-85">para saber si necesitas nuestra ayuda</span>
            </span>
          </Link>
          {/* Acceso discreto para pacientes con cita — también en móvil */}
          <div className="pt-3 mt-2 border-t" style={{ borderColor: "var(--sage-100)" }}>
            <Link
              to="/cuestionarios"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 font-sans-body text-xs uppercase tracking-widest"
              style={{ color: "var(--sage-600)" }}
              data-testid="mobile-nav-patients-link"
            >
              <ClipboardList size={14} />
              <span>¿Tienes cita? · Rellena tu cuestionario</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
